/**
 * KDS Order Card Component
 * Displays an order with its items for kitchen display systems
 * Supports USB printing via connected printers
 */
import { motion } from 'framer-motion';
import { 
  Coffee, 
  ChefHat, 
  GlassWater, 
  Check, 
  Package,
  User,
  Table2,
  Printer,
  Cake
} from 'lucide-react';
import OrderTimer from './OrderTimer';

// Station icons - matches backend station routing
const STATION_ICONS = {
  barista: Coffee,
  kitchen: ChefHat,
  juicer: GlassWater,
  counter: Cake
};

export default function KDSOrderCard({ 
  order, 
  onItemReady, 
  onItemGather,
  onItemUngather,
  onOrderComplete,
  onPrint,
  showGatherCheckbox = false,
  showReadyButton = true,
  showPrintButton = false,
  stationFilter = null
}) {
  const {
    order_id,
    order_number,
    customer_name,
    order_type,
    table_number,
    items = [],
    timer,
    created_at,
    all_gathered,
    can_complete
  } = order;

  // Filter items by station if specified
  const displayItems = stationFilter 
    ? items.filter(item => item.station === stationFilter)
    : items;

  if (displayItems.length === 0) return null;

  // Count item statuses
  const readyCount = displayItems.filter(i => i.kds_status === 'ready').length;
  const gatheredCount = displayItems.filter(i => i.kds_status === 'gathered').length;
  const pendingCount = displayItems.filter(i => i.kds_status === 'pending').length;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className={`
        bg-white dark:bg-gray-800 rounded-2xl overflow-hidden border-2
        ${timer?.flashing ? 'border-red-500 animate-pulse' : 
          timer?.color === 'orange' ? 'border-orange-400' :
          timer?.color === 'yellow' ? 'border-yellow-400' :
          'border-gray-200 dark:border-gray-700'}
      `}
    >
      {/* Header */}
      <div className={`
        px-4 py-3 flex items-center justify-between
        ${order_type === 'takeaway' 
          ? 'bg-orange-50 dark:bg-orange-900/20' 
          : 'bg-blue-50 dark:bg-blue-900/20'}
      `}>
        <div className="flex items-center gap-3">
          <div className="font-mono font-bold text-lg text-gray-900 dark:text-white">
            #{order_number}
          </div>
          <span className={`
            px-2 py-0.5 text-xs font-medium rounded-full
            ${order_type === 'takeaway' 
              ? 'bg-orange-200 text-orange-800 dark:bg-orange-800 dark:text-orange-200' 
              : 'bg-blue-200 text-blue-800 dark:bg-blue-800 dark:text-blue-200'}
          `}>
            {order_type === 'takeaway' ? 'TAKEAWAY' : 'DINE-IN'}
          </span>
          {table_number && (
            <span className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-300">
              <Table2 className="w-4 h-4" />
              Table {table_number}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {showPrintButton && onPrint && (
            <button
              onClick={onPrint}
              className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30 hover:bg-purple-200 dark:hover:bg-purple-800/50 text-purple-600 dark:text-purple-400 transition-colors"
              title="Print ticket"
            >
              <Printer className="w-4 h-4" />
            </button>
          )}
          <OrderTimer createdAt={created_at} orderType={order_type} />
        </div>
      </div>

      {/* Customer Name */}
      {customer_name && customer_name !== 'Guest' && (
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-700/50 flex items-center gap-2">
          <User className="w-4 h-4 text-gray-400" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{customer_name}</span>
        </div>
      )}

      {/* Items List */}
      <div className="p-4 space-y-2">
        {displayItems.map((item, idx) => {
          const Icon = STATION_ICONS[item.station] || Package;
          const isReady = item.kds_status === 'ready';
          const isGathered = item.kds_status === 'gathered';
          const isPending = item.kds_status === 'pending';
          
          return (
            <div
              key={idx}
              className={`
                flex items-center gap-3 p-3 rounded-xl transition-all
                ${isGathered ? 'bg-green-50 dark:bg-green-900/20' :
                  isReady ? 'bg-yellow-50 dark:bg-yellow-900/20' :
                  'bg-gray-50 dark:bg-gray-700/50'}
              `}
            >
              {/* Checkbox for gathering (Counter Display) */}
              {showGatherCheckbox && (isReady || isGathered) && (
                <button
                  onClick={() => isGathered ? onItemUngather?.(order_id, item.item_index) : onItemGather?.(order_id, item.item_index)}
                  className={`
                    w-8 h-8 rounded-lg flex items-center justify-center transition-all
                    ${isGathered 
                      ? 'bg-green-500 text-white' 
                      : 'bg-white dark:bg-gray-600 border-2 border-gray-300 dark:border-gray-500'}
                  `}
                >
                  {isGathered && <Check className="w-5 h-5" />}
                </button>
              )}
              
              {/* Station Icon */}
              <div className={`
                w-8 h-8 rounded-lg flex items-center justify-center
                ${item.station === 'barista' ? 'bg-blue-100 dark:bg-blue-900/30' :
                  item.station === 'kitchen' ? 'bg-red-100 dark:bg-red-900/30' :
                  item.station === 'counter' ? 'bg-pink-100 dark:bg-pink-900/30' :
                  'bg-yellow-100 dark:bg-yellow-900/30'}
              `}>
                <Icon className={`w-4 h-4 ${
                  item.station === 'barista' ? 'text-blue-600 dark:text-blue-400' :
                  item.station === 'kitchen' ? 'text-red-600 dark:text-red-400' :
                  item.station === 'counter' ? 'text-pink-600 dark:text-pink-400' :
                  'text-yellow-600 dark:text-yellow-400'
                }`} />
              </div>
              
              {/* Item Details */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900 dark:text-white">
                    {item.quantity > 1 && <span className="text-blue-600 dark:text-blue-400">{item.quantity}x </span>}
                    {item.name}
                  </span>
                  {item.size && (
                    <span className="text-xs px-1.5 py-0.5 bg-gray-200 dark:bg-gray-600 rounded text-gray-600 dark:text-gray-300">
                      {typeof item.size === 'object' ? (item.size.name || item.size.label || 'Custom') : item.size}
                    </span>
                  )}
                </div>
                {item.customizations && item.customizations.length > 0 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                    {item.customizations.map(c => c.name || c).join(', ')}
                  </p>
                )}
                {item.special_instructions && (
                  <p className="text-xs text-orange-600 dark:text-orange-400 mt-0.5 italic">
                    Note: {item.special_instructions}
                  </p>
                )}
              </div>
              
              {/* Ready Button (for prep stations) */}
              {showReadyButton && isPending && (
                <button
                  onClick={() => onItemReady?.(order_id, item.item_index)}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Ready
                </button>
              )}
              
              {/* Status Badge */}
              {(isReady || isGathered) && !showGatherCheckbox && (
                <span className={`
                  px-2 py-1 text-xs font-medium rounded-full
                  ${isGathered 
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' 
                    : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300'}
                `}>
                  {isGathered ? 'Gathered' : 'Ready'}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer with Complete Button (Counter Display) */}
      {showGatherCheckbox && (
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600 dark:text-gray-300">
              <span className="font-medium">{gatheredCount}</span>
              <span className="text-gray-400"> / {displayItems.length} items gathered</span>
            </div>
            
            <button
              disabled={!can_complete}
              onClick={() => onOrderComplete?.(order_id)}
              className={`
                px-6 py-2 font-medium rounded-xl transition-all
                ${can_complete 
                  ? 'bg-green-600 hover:bg-green-700 text-white' 
                  : 'bg-gray-200 dark:bg-gray-600 text-gray-400 cursor-not-allowed'}
              `}
            >
              {can_complete ? 'Mark Complete' : 'Gathering...'}
            </button>
          </div>
        </div>
      )}

      {/* Special Instructions Banner */}
      {order.special_instructions && (
        <div className="px-4 py-2 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 text-sm">
          <strong>Note:</strong> {order.special_instructions}
        </div>
      )}
    </motion.div>
  );
}
